'use client';
/**
 * LISTE DÉROULANTE DE COULEUR, AVEC PASTILLE (RQ, 02/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Remplace la rangée de pastilles de la fiche. Trois raisons :
 *
 *   · la valeur de départ est la couleur EFFECTIVE de la personne — celle que
 *     le planning montre déjà, y compris quand rien n'a jamais été choisi (le
 *     hachage de l'identifiant). Une rangée dont aucune pastille n'est
 *     sélectionnée disait « pas de couleur » alors qu'il y en avait une à
 *     l'écran ;
 *   · une couleur prise par un collègue de MÊME FONCTION s'y dit en toutes
 *     lettres (« prise par … ») au lieu d'une info-bulle qu'on ne lit pas ;
 *   · vingt-quatre pastilles côte à côte ne se distinguent plus, un nom oui.
 *
 * ⚠️ IL SERT AUSSI AUX POSTES depuis le 05/10/2026 (`sujet="poste"`) : même
 * palette, même garantie de contraste, même façon de dire qu'une teinte est
 * prise. Un second sélecteur ferait diverger l'apparence des deux fiches.
 *
 * ⚠️ LE COMPOSANT NE DÉCIDE RIEN. Il reçoit les teintes prises et les grise ;
 * le refus qui compte est celui du serveur (409), géré à l'enregistrement.
 * ⚠️ SES ÉCHAPPEMENTS PASSENT PAR `stopPropagation` : Échap ferme la liste, pas
 * la fiche qui la contient (qui n'a qu'une sortie, la croix).
 */
import React from 'react';
import { PALETTE, depuisTeinte, valeurCouleur } from '@/lib/couleurs-personne';
import { inputStyle } from '@/components/ui';

function Pastille({ h, texte, taille = 24 }: { h: number; texte: string; taille?: number }) {
  const p = depuisTeinte(h);
  return (
    <span aria-hidden style={{
      width: taille, height: taille, flex: 'none', borderRadius: '50%', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)',
      fontSize: Math.round(taille * 0.38), fontWeight: 700, overflow: 'hidden',
      background: p.background, color: p.color, border: `1.5px solid ${p.borderColor}`,
    }}>{texte}</span>
  );
}

export function SelecteurCouleur({ teinte, initiales, prises, onChoisir, id, sujet = 'personne', messagePartage, declencheur, ouvertAuMontage = false }: {
  /** La teinte EFFECTIVE du sujet (choisie, sinon hachage). */
  teinte: number;
  /** Texte de la pastille — les initiales de la personne, l'abrégé du poste. */
  initiales: string;
  /** teinte → nom de celui (collègue de même fonction, autre poste) qui la porte. */
  prises: Map<number, string>;
  /** Reçoit la valeur à STOCKER (`h<teinte>`). */
  onChoisir: (valeur: string) => void;
  id?: string;
  /** Ce que la couleur désigne — nomme la liste pour les lecteurs d'écran
   *  (05/10/2026 : le même sélecteur sert aux POSTES, et « Couleur de la
   *  personne » annoncé sur un poste serait faux). */
  sujet?: 'personne' | 'poste';
  /** L'avertissement quand la teinte est DÉJÀ PORTÉE. Le défaut parle des
   *  collègues de même fonction ; un poste ne se compare pas à une fonction. */
  messagePartage?: (porteur: string) => React.ReactNode;
  /**
   * CE SUR QUOI ON APPUIE, quand ce n'est pas le champ (RQ, 05/10/2026).
   *
   * Au dossier RH, c'est le ROND AUX INITIALES de la personne : « juste
   * permettre de changer la couleur en appuyant sur le rond ». La couleur est
   * la seule propriété dont l'objet qui la porte est déjà à l'écran — un champ
   * « Couleur » à côté d'une pastille colorée redit ce que la pastille montre.
   *
   * Fourni, le champ disparaît : le déclencheur devient le bouton, la liste
   * s'ouvre sous lui, et l'avertissement de teinte partagée passe EN TÊTE DE
   * LA LISTE plutôt que sous le bouton — sous un rond pris dans un en-tête, il
   * en aurait poussé la mise en page à chaque conflit.
   */
  declencheur?: React.ReactNode;
  /** Ouvre la liste dès l'affichage — pour l'hôte qui vient d'être ouvert PAR
   *  ce geste : sans cela, le clic sur le rond ferait entrer en modification
   *  sans montrer la palette, et il faudrait cliquer une seconde fois. */
  ouvertAuMontage?: boolean;
}) {
  const [ouvert, setOuvert] = React.useState(ouvertAuMontage);
  const [actif, setActif] = React.useState(0);
  const boite = React.useRef<HTMLDivElement | null>(null);

  /* La teinte courante peut ne pas figurer à la palette (hachage de
     l'identifiant) : elle reste sélectionnable, en tête, sans quoi ouvrir la
     fiche la remplacerait en silence par la première de la liste. */
  const horsPalette = !PALETTE.some((c) => c.teinte === teinte);
  const entrees = React.useMemo(() => [
    ...(horsPalette ? [{ teinte, nom: 'Couleur actuelle (calculée)' }] : []),
    ...PALETTE,
  ], [horsPalette, teinte]);
  const courante = entrees.find((e) => e.teinte === teinte) || entrees[0];
  const priseParAutre = (t: number) => (t === teinte ? undefined : prises.get(t));
  const partagee = prises.get(teinte);

  React.useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e: MouseEvent) => {
      if (boite.current && !boite.current.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);

  const ouvrir = () => {
    setActif(Math.max(0, entrees.findIndex((e) => e.teinte === teinte)));
    setOuvert(true);
  };
  const choisir = (t: number) => {
    if (priseParAutre(t)) return;
    setOuvert(false);
    if (t !== teinte) onChoisir(valeurCouleur(t));
  };
  const clavier = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && ouvert) { e.stopPropagation(); setOuvert(false); return; }
    if (!ouvert) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrir(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActif((i) => Math.min(entrees.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActif((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choisir(entrees[actif].teinte); }
  };

  const nomListe = sujet === 'poste' ? 'Couleur du poste' : 'Couleur de la personne';
  const avertissement = partagee && (
    <>{messagePartage
      ? messagePartage(partagee)
      : <>Cette couleur est aussi celle de {partagee}, de même fonction : les deux se confondront au
        planning. Choisissez-en une autre.</>}</>
  );

  return (
    <div ref={boite} style={{ position: 'relative', maxWidth: declencheur ? undefined : 320, display: declencheur ? 'inline-block' : undefined }} onKeyDown={clavier}>
      {declencheur ? (
        /* Le déclencheur est rendu TEL QUEL : aucun habillage de bouton par
           dessus — un rond de 52 px dans une bordure rectangulaire cesserait
           d'être le rond qu'on voit au planning. Il garde l'anneau de focus du
           socle (`ged-btn`), sans quoi il serait invisible au clavier. */
        <button type="button" id={id} className="ged-btn" aria-haspopup="listbox" aria-expanded={ouvert}
          aria-label={`${nomListe} — changer`}
          title={partagee ? `Changer la couleur — déjà portée par ${partagee}` : 'Changer la couleur'}
          onClick={() => (ouvert ? setOuvert(false) : ouvrir())}
          style={{ background: 'none', border: 'none', padding: 0, borderRadius: '50%', cursor: 'pointer', display: 'inline-flex' }}>
          {declencheur}
        </button>
      ) : (
        <button type="button" id={id} className="ged-btn" aria-haspopup="listbox" aria-expanded={ouvert}
          onClick={() => (ouvert ? setOuvert(false) : ouvrir())}
          style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 9, width: '100%', cursor: 'pointer', textAlign: 'start' }}>
          <Pastille h={teinte} texte={initiales} />
          <span style={{ flex: 1 }}>{courante.nom}</span>
          <span aria-hidden style={{ color: 'var(--text-muted)', fontSize: 11 }}>▾</span>
        </button>
      )}
      {ouvert && (
        <ul role="listbox" aria-label={nomListe} style={{
          position: 'absolute', insetInlineStart: 0, insetBlockStart: 'calc(100% + 4px)', zIndex: 20, margin: 0,
          padding: 4, listStyle: 'none',
          /* Sur un déclencheur étroit (le rond), la liste ne peut pas faire
             « 100 % » de lui : elle porte alors sa propre largeur. */
          width: declencheur ? 300 : '100%', maxWidth: '80vw', maxHeight: 280, overflowY: 'auto',
          background: 'var(--surface-card)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 28px rgba(15,23,42,0.18)',
        }}>
          {declencheur && avertissement && (
            <li role="presentation" style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45, padding: '6px 8px' }}>
              {avertissement}
            </li>
          )}
          {entrees.map((e, i) => {
            const par = priseParAutre(e.teinte);
            return (
              <li key={e.teinte} role="option" aria-selected={e.teinte === teinte} aria-disabled={!!par}
                onMouseEnter={() => setActif(i)}
                onClick={() => choisir(e.teinte)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 9, padding: '5px 8px', borderRadius: 6,
                  cursor: par ? 'not-allowed' : 'pointer', fontSize: 13,
                  opacity: par ? 0.5 : 1,
                  background: i === actif ? 'var(--gray-50)' : 'transparent',
                  fontWeight: e.teinte === teinte ? 700 : 400,
                }}>
                <Pastille h={e.teinte} texte={initiales} taille={22} />
                <span style={{ flex: 1 }}>{e.nom}</span>
                {par && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>prise par {par}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {!declencheur && avertissement && (
        <div role="status" style={{ marginTop: 5, fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45 }}>
          {avertissement}
        </div>
      )}
    </div>
  );
}
